// TAPINTO® Editor Frontend - Editor Store
// Version: 1.0
// Date: 2025-10-01

import { create } from 'zustand';
import { EditorState, Page, PageStructure, CreatePageRequest, UpdatePageRequest } from '../types';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';

interface EditorStore extends EditorState {
  loadPage: (pageId: string) => Promise<boolean>;
  createPage: (pageData: CreatePageRequest) => Promise<boolean>;
  updatePage: (updates: UpdatePageRequest) => Promise<boolean>;
  savePage: () => Promise<boolean>;
  publishPage: () => Promise<boolean>;
  setSelectedComponent: (componentId: string | null) => void;
  setDirty: (isDirty: boolean) => void;
  setSaving: (isSaving: boolean) => void;
  setPublishing: (isPublishing: boolean) => void;
  clearEditor: () => void;
  autoSave: () => Promise<void>;
}

let autoSaveInterval: number | null = null;

export const useEditorStore = create<EditorStore>((set, get) => ({
  currentPage: null,
  selectedComponent: null,
  isDirty: false,
  isSaving: false,
  isPublishing: false,
  lastSaved: undefined,

  loadPage: async (pageId: string) => {
    try {
      const response = await apiService.getPage(pageId);
      
      if (response.success && response.data) {
        set({
          currentPage: response.data,
          selectedComponent: null,
          isDirty: false,
          lastSaved: response.data.updated_at,
        });
        
        // Start auto-save interval
        get().startAutoSave();
        return true;
      } else {
        toast.error(response.error || 'Failed to load page');
        return false;
      }
    } catch (error) {
      console.error('Load page error:', error);
      toast.error('Failed to load page');
      return false;
    }
  },

  createPage: async (pageData: CreatePageRequest) => {
    try {
      const response = await apiService.createPage(pageData);
      
      if (response.success && response.data) {
        // Load the created page
        const loaded = await get().loadPage(response.data.page_id);
        if (loaded) {
          toast.success('Page created successfully');
        }
        return loaded;
      } else {
        toast.error(response.error || 'Failed to create page');
        return false;
      }
    } catch (error) {
      console.error('Create page error:', error);
      toast.error('Failed to create page');
      return false;
    }
  },

  updatePage: async (updates: UpdatePageRequest) => {
    const { currentPage } = get();
    if (!currentPage) return false;

    try {
      const response = await apiService.updatePage(currentPage.page_id, updates);
      
      if (response.success && response.data) {
        set({
          currentPage: {
            ...currentPage,
            ...updates,
            version: response.data.version,
            updated_at: response.data.updated_at,
          },
          isDirty: false,
          lastSaved: response.data.updated_at,
        });
        
        toast.success('Page saved successfully');
        return true;
      } else {
        toast.error(response.error || 'Failed to save page');
        return false;
      }
    } catch (error) {
      console.error('Update page error:', error);
      toast.error('Failed to save page');
      return false;
    }
  },

  savePage: async () => {
    const { currentPage, isSaving } = get();
    if (!currentPage || isSaving) return false;

    set({ isSaving: true });

    try {
      const success = await get().updatePage({
        structure: currentPage.structure,
        change_description: 'Manual save',
      });
      
      set({ isSaving: false });
      return success;
    } catch (error) {
      set({ isSaving: false });
      return false;
    }
  },

  publishPage: async () => {
    const { currentPage, isPublishing } = get();
    if (!currentPage || isPublishing) return false;

    set({ isPublishing: true });

    try {
      // Save first
      const saved = await get().savePage();
      if (!saved) {
        set({ isPublishing: false });
        return false;
      }

      // Then publish
      const response = await apiService.publishPage(currentPage.page_id);
      
      if (response.success && response.data) {
        set({
          currentPage: {
            ...currentPage,
            status: 'published',
            published_at: response.data.published_at,
          },
          isPublishing: false,
        });
        
        toast.success('Page published successfully!');
        return true;
      } else {
        toast.error(response.error || 'Failed to publish page');
        set({ isPublishing: false });
        return false;
      }
    } catch (error) {
      console.error('Publish page error:', error);
      toast.error('Failed to publish page');
      set({ isPublishing: false });
      return false;
    }
  },

  setSelectedComponent: (componentId: string | null) => {
    set({ selectedComponent: componentId });
  },

  setDirty: (isDirty: boolean) => {
    set({ isDirty });
  },

  setSaving: (isSaving: boolean) => {
    set({ isSaving });
  },

  setPublishing: (isPublishing: boolean) => {
    set({ isPublishing });
  },

  clearEditor: () => {
    if (autoSaveInterval) {
      clearInterval(autoSaveInterval);
      autoSaveInterval = null;
    }
    
    set({
      currentPage: null,
      selectedComponent: null,
      isDirty: false,
      isSaving: false,
      isPublishing: false,
      lastSaved: undefined,
    });
  },

  autoSave: async () => {
    const { currentPage, isDirty, isSaving } = get();
    
    if (!currentPage || !isDirty || isSaving) return;

    try {
      await get().updatePage({
        structure: currentPage.structure,
        change_description: 'Auto-save',
      });
    } catch (error) {
      console.error('Auto-save error:', error);
    }
  },

  startAutoSave: () => {
    if (autoSaveInterval) {
      clearInterval(autoSaveInterval);
    }
    
    autoSaveInterval = setInterval(() => {
      get().autoSave();
    }, 30000); // 30 seconds
  },
}));
